import { AppDate } from '../envTypes';

import { ModelBase } from './base';
import { Address } from './address';
import { StoreFront } from './store';

// ISO types
export type ISO8601DateTimeString = string;
export type ISO3166Alpha2CountryCode = string;
export type ISO4217CurrencyCode = string;

// ============================================
// Package Template Types
// ============================================

export enum PackageTemplateType {
  /**
   * @deprecated
   */
  Custom = 'CUSTOM',
  /**
   * @deprecated
   */
  Preset = 'PRESET',
  GLOBAL = 'GLOBAL',
  SELLER_CUSTOM = 'SELLER_CUSTOM',
  SELLER_ARRANGE_MY_OWN = 'SELLER_ARRANGE_MY_OWN',
}

export enum DistanceUnit {
  'cm' = 'cm',
  'in' = 'in',
  'ft' = 'ft',
  'm' = 'm',
  'mm' = 'mm',
  'yd' = 'yd',
}

export enum MassUnit {
  'g' = 'g',
  'kg' = 'kg',
  'lb' = 'lb',
  'oz' = 'oz',
}

/**
 * @deprecated since 12/06/22
 */
export enum PackageType {
  XS = 'XS',
  Small = 'Small',
  Medium = 'Med',
  L = 'L',
  XL = 'XL',
  XXL = 'XXL',
  Other = 'Other',
}

/**
 * @deprecated since 12/06/22
 */
export enum USPSShippingServiceLevel {
  CHEAPEST_AVAILABLE = 'CHEAPEST_AVAILABLE',
  FASTEST_AVAILABLE = 'FASTEST_AVAILABLE',
  USPS_PARCEL_SELECT = 'USPS_PARCEL_SELECT',
  USPS_FIRST_CLASS = 'USPS_FIRST_CLASS',
  USPS_PRIORITY = 'USPS_PRIORITY',
  USPS_PRIORITY_EXPRESS = 'USPS_PRIORITY_EXPRESS',
}

/**
 * @deprecated since 12/06/22
 */
export enum SignatureConfirmationType {
  Standard = 'STANDARD',
  Adult = 'ADULT',
  Certified = 'CERTIFIED',
}

/**
 * @deprecated since 12/06/22
 */
export enum SignatureConfirmation {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED',
  INDIRECT = 'INDIRECT',
  CARRIER_CONFIRMATION = 'CARRIER_CONFIRMATION',
}

/**
 * @deprecated since 12/06/22
 */
export enum InsurancePolicyType {
  minOrderGroupInsureAmount = 'MIN_ORDER_GROUP_INSURE_AMOUNT',
  disable = 'DISABLE',
}

/**
 * @deprecated since 12/06/22
 */
export type InsurancePolicy =
  | { type: InsurancePolicyType.minOrderGroupInsureAmount; amount: number }
  | { type: InsurancePolicyType.disable };

export enum Carrier {
  SHIPPO = 'SHIPPO', // test only
  USPS = 'USPS',
  UPS = 'UPS',
  CANADA_POST = 'CANADA_POST',
  DHL_EXPRESS = 'DHL_EXPRESS',
  DPD_UK = 'DPD_UK',
  HERMES_UK = 'HERMES_UK',
}

/**
 * @deprecated since 12/06/22
 * @description carrierPackageTemplates/{id}
 */
export interface CarrierPackageTemplate extends ModelBase {
  carrier: Carrier;
  name: string;
  token: string;
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
  weight: number;
  massUnit: MassUnit;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };
}

/**
 * @deprecated since 12/06/22
 * @description /carrierPackageTemplates/{autoId}
 */
export interface PresetPackageTemplate extends CarrierPackageTemplate {
  type: PackageTemplateType.Preset;
  isDefault: boolean;
}

/**
 * @deprecated since 12/06/22
 * @description /globalPackageTemplates/{autoId}, these templates are managed by PSL
 */
export interface GlobalPackageTemplate extends ModelBase {
  type: PackageTemplateType.GLOBAL;
  name: string;
  width: number;
  height: number;
  depth: number;
  distanceUnit: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  isDefault: boolean;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };
  carrier: Carrier;
  imageUrl?: string;
  description?: string;
  shippingPrice: number;
  shippingCost?: string;
  packageType?: PackageType;
  serviceLevel?: USPSShippingServiceLevel;
  signatureConfirmation?: SignatureConfirmation;
}

/**
 * @deprecated since 12/06/22
 * @description /stores/{storeId}/packageTemplates/{packageTemplateId}, these templates are managed by sellers
 */
export interface CustomPackageTemplate extends ModelBase {
  type: PackageTemplateType.Custom;
  name: string;
  width: number;
  height: number;
  depth: number;
  distanceUnit: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  isDefault: boolean;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };
  isAutoCalculatePackageWeight?: boolean;
}

export interface PackageTemplateUnit {
  distanceUnit: DistanceUnit;
  massUnit: MassUnit;
}

export interface PackageTemplate extends ModelBase {
  type: PackageTemplateType;
  isDefault: boolean;
  imageUrl?: string;
  name: string;
  description?: string;
  shippingPrice: number;
  shippingCost?: string;
  carrierId?: string;
  carrier?: ShippoCarrierOption;
  parcelTemplateId?: string;
  parcelTemplate?: ShippoCarrierParcelTemplateOption;
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  signatureConfirmationId?: string;
  signatureConfirmation?: ShippoCarrierShipmentExtrasSignatureConfirmationOption;
  serviceLevelId?: string;
  serviceLevel?: ShippoCarrierPopshopServiceLevelOption;
  countUsedAsDefault?: number;
  storeId?: string;
  store?: StoreFront;
}

// ============================================
// Shippo Carrier Configuration Types
// ============================================

export enum ShippoCarrierToken {
  USPS = 'usps',
}

export enum ShippoCarrierName {
  USPS = 'USPS',
}

export interface ShippoCarrierOption {
  ordinal: number;
  token: ShippoCarrierToken;
  name: ShippoCarrierName;
  alias?: string;
}

export enum ShippoCarrierPopshopParcelTemplateType {
  CUSTOM = 'CUSTOM',
  SHIPPO_CARRIER_PARCEL_TEMPLATE = 'SHIPPO_CARRIER_PARCEL_TEMPLATE',
}

export enum ShippoCarrierParcelTemplateId {
  ENVELOPE = 'Envelope',
  PACKAGE = 'Package',
  USPS_REGIONAL_RATE_BOX_1 = 'USPS_RegionalRateBoxB1',
  USPS_REGIONAL_RATE_BOX_2 = 'USPS_RegionalRateBoxB2',
  USPS_IRREGULAR_PARCEL = 'USPS_IrregularParcel',
  USPS_CARD = 'USPS_Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'USPS_FlatRateCardboardEnvelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'USPS_FlatRateLegalEnvelope',
  USPS_FLAT_RATE_ENVELOPE = 'USPS_FlatRateEnvelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'USPS_FlatRateGiftCardEnvelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = 'USPS_LargeVideoFlatRateBox',
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'USPS_LargeFlatRateBoardGameBox',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'USPS_SmallFlatRateEnvelope',
  USPS_SOFT_PACK = 'USPS_SoftPack',
  USPS_LARGE_FLAT_RATE_BOX = 'USPS_LargeFlatRateBox',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'USPS_MediumFlatRateBox1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'USPS_MediumFlatRateBox2',
  USPS_SMALL_FLAT_RATE_BOX = 'USPS_SmallFlatRateBox',
  USPS_REGIONAL_RATE_BOX_A1 = 'USPS_RegionalRateBoxA1',
  USPS_REGIONAL_RATE_BOX_A2 = 'USPS_RegionalRateBoxA2',
  USPS_APO_FLAT_RATE_BOX = 'USPS_APOFlatRateBox',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'USPS_FlatRateWindowEnvelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'USPS_FlatRatePaddedEnvelope',
}

export enum ShippoCarrierParcelTemplateToken {
  USPS_REGIONAL_RATE_BOX_1 = 'USPS_RegionalRateBoxB1',
  USPS_REGIONAL_RATE_BOX_2 = 'USPS_RegionalRateBoxB2',
  USPS_IRREGULAR_PARCEL = 'USPS_IrregularParcel',
  USPS_CARD = 'USPS_Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'USPS_FlatRateCardboardEnvelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'USPS_FlatRateLegalEnvelope',
  USPS_FLAT_RATE_ENVELOPE = 'USPS_FlatRateEnvelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'USPS_FlatRateGiftCardEnvelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = 'USPS_LargeVideoFlatRateBox',
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'USPS_LargeFlatRateBoardGameBox',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'USPS_SmallFlatRateEnvelope',
  USPS_SOFT_PACK = 'USPS_SoftPack',
  USPS_LARGE_FLAT_RATE_BOX = 'USPS_LargeFlatRateBox',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'USPS_MediumFlatRateBox1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'USPS_MediumFlatRateBox2',
  USPS_SMALL_FLAT_RATE_BOX = 'USPS_SmallFlatRateBox',
  USPS_REGIONAL_RATE_BOX_A1 = 'USPS_RegionalRateBoxA1',
  USPS_REGIONAL_RATE_BOX_A2 = 'USPS_RegionalRateBoxA2',
  USPS_APO_FLAT_RATE_BOX = 'USPS_APOFlatRateBox',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'USPS_FlatRateWindowEnvelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'USPS_FlatRatePaddedEnvelope',
}

export enum ShippoCarrierParcelTemplateName {
  ENVELOPE = 'Envelope',
  PACKAGE = 'Package',
  USPS_REGIONAL_RATE_BOX_1 = 'Regional Rate Box B1',
  USPS_REGIONAL_RATE_BOX_2 = 'Regional Rate Box B2',
  USPS_IRREGULAR_PARCEL = 'Irregular Parcel',
  USPS_CARD = 'Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'Flat Rate Cardboard Envelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'Flat Rate Legal Envelope',
  USPS_FLAT_RATE_ENVELOPE = 'Flat Rate Envelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'Flat Rate Gift Card Envelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = "Flat Rate Large Video Box (Int'l only)",
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'Large Flat Rate Board Game Box',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'Small Flat Rate Envelope',
  USPS_SOFT_PACK = 'Soft Pack Padded Envelope',
  USPS_LARGE_FLAT_RATE_BOX = 'Large Flat Rate Box',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'Medium Flat Rate Box 1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'Medium Flat Rate Box 2',
  USPS_SMALL_FLAT_RATE_BOX = 'Small Flat Rate Box',
  USPS_REGIONAL_RATE_BOX_A1 = 'Regional Rate Box A1',
  USPS_REGIONAL_RATE_BOX_A2 = 'Regional Rate Box A2',
  USPS_APO_FLAT_RATE_BOX = 'APO/FPO/DPO Large Flat Rate Box',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'Flat Rate Window Envelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'Flat Rate Padded Envelope',
}

export interface ShippoCarrierParcelTemplateOption {
  ordinal: number;
  type: ShippoCarrierPopshopParcelTemplateType;
  token?: ShippoCarrierParcelTemplateToken;
  name: ShippoCarrierParcelTemplateName;
  alias?: string;
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
}

export enum ShippoCarrierShipmentExtrasSignatureConfirmationToken {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED',
}

export enum ShippoCarrierShipmentExtrasSignatureConfirmationName {
  STANDARD = 'Signature Confirmation',
  ADULT = 'Adult Signature Required',
  CERTIFIED = 'Certified Mail',
}

export interface ShippoCarrierShipmentExtrasSignatureConfirmationOption {
  ordinal: number;
  token: ShippoCarrierShipmentExtrasSignatureConfirmationToken;
  name: ShippoCarrierShipmentExtrasSignatureConfirmationName;
  alias?: string;
}

export enum ShippoCarrierServiceLevelToken {
  USPS_GROUND_ADVANTAGE = 'usps_ground_advantage',
  USPS_PRIORITY = 'usps_priority',
  USPS_PRIORITY_EXPRESS = 'usps_priority_express',
}

export enum ShippoCarrierServiceLevelName {
  USPS_GROUND_ADVANTAGE = 'Ground Advantage',
  USPS_PRIORITY = 'Priority Mail',
  USPS_PRIORITY_EXPRESS = 'Priority Mail Express',
}

export enum ShippoCarrierPopshopServiceLevelType {
  SHIPPO_RATE_ATTRIBUTE = 'SHIPPO_RATE_ATTRIBUTE',
  SHIPPO_CARRIER_SERVICE_LEVEL = 'SHIPPO_CARRIER_SERVICE_LEVEL',
}

export enum ShippoCarrierPopshopServiceLevelToken {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  USPS_GROUND_ADVANTAGE = 'usps_ground_advantage',
  USPS_PRIORITY = 'usps_priority',
  USPS_PRIORITY_EXPRESS = 'usps_priority_express',
}

export enum ShippoCarrierPopshopServiceLevelName {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  USPS_GROUND_ADVANTAGE = 'Ground Advantage',
  USPS_PRIORITY = 'Priority Mail',
  USPS_PRIORITY_EXPRESS = 'Priority Mail Express',
}

export interface ShippoCarrierPopshopServiceLevelOption {
  ordinal: number;
  type: ShippoCarrierPopshopServiceLevelType;
  token: ShippoCarrierPopshopServiceLevelToken;
  name: ShippoCarrierPopshopServiceLevelName;
  alias?: string;
}

// ============================================
// Shippo API Object Types
// ============================================

export interface ShippoAddress {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  name: string;
  company: string;
  country: ISO3166Alpha2CountryCode;
  state: string;
  city: string;
  street_no: string;
  street1: string;
  street2: string;
  street3: string;
  zip: string;
  phone: string;
  email: string;
  is_complete: boolean;
  is_residential?: boolean;
  metadata: string;
  test: boolean;
  validate?: boolean | null;
  validation_results: {
    is_valid?: boolean;
    messages?: {
      source?: string;
      code?: string;
      type?: string;
      text?: string;
    }[];
  };
}

export enum ShippoParcelState {
  VALID = 'VALID',
}

export interface ShippoParcel {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  object_state: ShippoParcelState;
  length: string;
  width: string;
  height: string;
  distance_unit: DistanceUnit;
  weight: string;
  mass_unit: MassUnit;
  value_amount: string | null;
  value_currency: ISO4217CurrencyCode | null;
  metadata: string;
  template: string | null;
  extra: {
    COD?: {
      amount: string;
      currency: ISO4217CurrencyCode;
      payment_method?: 'SECURED_FUNDS' | 'CASH' | 'ANY';
    };
    insurance?: {
      amount: string;
      currency: ISO4217CurrencyCode;
      provider?: 'FEDEX' | 'UPS' | 'UDS';
      content?: string;
    };
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  line_items: any[];
  test: boolean;
}

export interface ShippoServicelevel {
  name?: string;
  token?: string;
  terms?: string;
  extended_token?: string;
  parent_servicelevel?: ShippoServicelevel | null;
}

export enum ShippoRateAttribute {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  BESTVALUE = 'BESTVALUE',
}

export interface ShippoRate {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  shipment: string;
  amount: string;
  currency: string;
  amount_local: ISO4217CurrencyCode;
  currency_local: ISO4217CurrencyCode;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: ShippoServicelevel;
  estimated_days: number;
  duration_terms: string;
  carrier_account: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arrives_by: any;
  attributes: ShippoRateAttribute[];
  messages: {
    code?: string;
    message?: string;
  }[];
  test: boolean;
  zone: string;
}

export enum ShippoShipmentStatus {
  WAITING = 'WAITING',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export enum ShippoShipmentExtraSignatureConfirmation {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED',
  INDIRECT = 'INDIRECT',
  CARRIER_CONFIRMATION = 'CARRIER_CONFIRMATION',
}

export interface ShippoShipment {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoShipmentStatus;
  address_return: Address;
  address_from: Address;
  address_to: Address;
  alternate_address_to: Address | null;
  parcels: ShippoParcel[];
  shipment_date: ISO8601DateTimeString;
  customs_declaration: string | null;
  carrier_accounts: string[];
  async: boolean;
  metadata: string;
  extra: {
    signature_confirmation?: ShippoShipmentExtraSignatureConfirmation;
    authority_to_leave?: boolean;
    saturday_delivery?: boolean;
    bypass_address_validation?: boolean;
    request_retail_rates?: boolean;
    customer_branch?: string;
    premium?: boolean;
    preferred_delivery_timeframe?:
      | '10001200'
      | '12001400'
      | '14001600'
      | '16001800'
      | '18002000'
      | '19002100';
    lasership_attrs?:
      | 'TwoPersonDelivery'
      | 'Explosive'
      | 'Hazmat'
      | 'ControlledSubstance'
      | 'Refrigerated'
      | 'Perishable'
      | 'NoRTS';
    lasership_declared_value?: string;
    container_type?: string;
    billing?: {
      type?: 'SENDER' | 'RECIPIENT' | 'THIRD_PARTY' | 'THIRD_PARTY_CONSIGNEE';
      account?: string;
      zip?: string;
      country?: ISO3166Alpha2CountryCode;
      participation_code?: string;
    };
    COD?: {
      amount?: string;
      currency?: ISO4217CurrencyCode;
      payment_method?: 'SECURED_FUNDS' | 'CASH' | 'ANY';
    };
    alcohol?: {
      contains_alcohol?: boolean;
      recipient_type?: 'licensee' | 'consumer';
    };
    dry_ice?: {
      contains_dry_ice?: boolean;
      weight?: string;
    };
    insurance?: {
      amount?: string;
      currency?: ISO4217CurrencyCode;
      content?: string;
      provider?: 'FEDEX' | 'UPS' | 'ONTRAC';
    };
    dangerous_goods_code?:
      | '01'
      | '02'
      | '03'
      | '04'
      | '05'
      | '06'
      | '07'
      | '08'
      | '09';
    is_return?: boolean;
    reference_1?: string;
    reference_2?: string;
    return_service_type?:
      | 'PRINT_AND_MAIL'
      | 'ATTEMPT_1'
      | 'ATTEMPT_3'
      | 'ELECTRONIC_LABEL';
    qr_code_requested?: boolean;
    ancillary_endorsement?:
      | 'FORWARDING_SERVICE_REQUESTED'
      | 'RETURN_SERVICE_REQUESTED';
    carbon_neutral?: boolean;
    delivery_instructions?: string;
    fulfillment_center?: string;
    carrier_hub_id?: string;
    critical_pull_time?: string;
    carrier_hub_travel_time?: number;
  };
  rates: ShippoRate[];
  messages: {
    code?: string;
    message?: string;
  }[];
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order: any;
}

export enum ShippoTransactionState {
  VALID = 'VALID',
  INVALID = 'INVALID',
}

export enum ShippoTransactionStatus {
  WAITING = 'WAITING',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  REFUNDED = 'REFUNDED',
  REFUNDPENDING = 'REFUNDPENDING',
  REFUNDREJECTED = 'REFUNDREJECTED',
}

export enum ShippoTrackingRecordStatus {
  UNKNOWN = 'UNKNOWN',
  PRE_TRANSIT = 'PRE_TRANSIT',
  DELIVERED = 'DELIVERED',
  TRANSIT = 'TRANSIT',
  FAILURE = 'FAILURE',
  RETURNED = 'RETURNED',
}

export interface ShippoTransaction {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  object_state: ShippoTransactionState;
  status: ShippoTransactionStatus;
  parcel: string;
  rate: string;
  tracking_number: string;
  tracking_status: ShippoTrackingRecordStatus;
  tracking_url_provider: string;
  eta: string | null;
  label_url: string;
  commercial_invoice_url: string;
  messages: {
    code?: string;
    message?: string;
  }[];
  metadata: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  billing: any;
  qr_code_url: string;
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order: any;
}

export interface ShippoTrackingRecord {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoTrackingRecordStatus;
  status_details: string;
  status_date: ISO8601DateTimeString;
  substatus?: {
    action_required?: boolean;
    code?: string;
    text?: string;
  };
  location: {
    country: ISO4217CurrencyCode;
    state: string;
    city: string;
    zip: string;
  };
}

export interface ShippoTrackingStatus {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  carrier: string;
  tracking_number: string;
  address_from: ShippoAddress;
  address_to: ShippoAddress;
  transaction: string;
  eta: ISO8601DateTimeString | null;
  original_eta: ISO8601DateTimeString | null;
  servicelevel: ShippoServicelevel;
  metadata: string;
  tracking_status: ShippoTrackingRecord;
  tracking_history: ShippoTrackingRecord[];
  messages: {
    code?: string;
    message?: string;
  }[];
}

export enum ShippoRefundStatus {
  QUEUED = 'QUEUED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface ShippoRefund {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoRefundStatus;
  transaction: string;
  test: boolean;
}

// ============================================
// Shipment Types
// ============================================

export interface Shipment {
  buyerId: string;
  sellerId: string;
  orderId: string;
  shippoTransactionId: string;
  labelUrl: string;
  trackingNumber: string;
  trackingStatus: string;
  trackingUrlProvider: string;
  amount: number;
  margin?: number;
  transaction: string;
  transactionStatus: string;
  createdAt: AppDate;
  updateAt: AppDate;
  carrier?: Carrier;
  platform?: {
    merchantId: string;
    rateId: string;
  };
  stripeTransferId?: string | null;
}

export type DeleteShippingAddress = { deleteThis: true; pos?: number };
export type UpdateShippingAddress = Address & {
  deleteThis?: false;
  pos?: number;
  asDefault?: boolean;
};
export type UpdateOrDeleteShippingAddress =
  | DeleteShippingAddress
  | UpdateShippingAddress;
